import { defineStore } from 'pinia';
import { ref } from 'vue';
import api from '../services/api';
import { authenticatedFetch } from '@/utils/apiHelper';

/**
 * @typedef {Object} SiteContact
 * @property {number} id - Contact ID
 * @property {number} site_id - Site ID
 * @property {string} name - Contact name
 * @property {string} email - Contact email
 * @property {string} [phone] - Contact phone number
 * @property {string} [role] - Contact role
 */

/**
 * @typedef {Object} Site
 * @property {number} id - Site ID
 * @property {string} name - Site name
 * @property {string} address - Site address
 * @property {string} [city] - City
 * @property {string} [state] - State
 * @property {string} [postcode] - Postcode
 * @property {number|null} customer_id - Customer ID
 * @property {string|null} created_at - Creation timestamp
 * @property {string|null} updated_at - Update timestamp
 * @property {SiteContact[]} [contacts] - Site contacts
 */

/**
 * @typedef {Object} PaginationInfo
 * @property {number} currentPage - Current page number
 * @property {number} perPage - Items per page
 * @property {number} totalItems - Total number of items
 * @property {number} totalPages - Total number of pages
 */

/**
 * @typedef {Object} SitesState
 * @property {import('vue').Ref<Site[]>} sites - List of sites
 * @property {import('vue').Ref<boolean>} loading - Loading state
 * @property {import('vue').Ref<string|null>} error - Error message if any
 * @property {import('vue').Ref<PaginationInfo>} pagination - Pagination information
 */

/**
 * @typedef {Object} SitesMethods
 * @property {(options?: Object) => Promise<Site[]>} fetchSites - Fetch sites with optional filters
 * @property {(siteData: Partial<Site>) => Promise<Site>} createSite - Create new site
 * @property {(id: number, updates: Partial<Site>) => Promise<Site>} updateSite - Update site
 * @property {(id: number) => Promise<void>} deleteSite - Delete site
 * @property {(siteId: number, contactData: Partial<SiteContact>) => Promise<SiteContact>} addContact - Add contact to site
 * @property {(siteId: number, contactId: number, contactData: Partial<SiteContact>) => Promise<SiteContact>} updateContact - Update site contact
 * @property {(siteId: number, contactId: number) => Promise<void>} removeContact - Remove contact from site
 * @property {(siteId: number) => Promise<boolean>} downloadBrickTTL - Download BRICK Schema TTL file for site
 */

/**
 * Sites store for managing site data, contacts, and BRICK Schema exports
 * @returns {SitesState & SitesMethods}
 */
export const useSitesStore = defineStore('sites', () => {
  const sites = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const pagination = ref({
    currentPage: 1,
    perPage: 20,
    totalItems: 0,
    totalPages: 0,
  });
  const API_URL = '/api';

  const fetchSites = async (options = {}) => {
    loading.value = true;
    error.value = null;
    try {
      // Build standardized parameters - don't include cache buster
      const params = { ...options };
      
      // Make the API request using standardized API client
      const result = await api.getPaginated('sites', params);
      
      let fetchedSites = [];
      let paginationInfo = null;
      
      // Result from api.getPaginated is already unwrapped in the standard format
      if (result && result.data && Array.isArray(result.data)) {
        fetchedSites = result.data;
        paginationInfo = result.pagination;
        
        // Update the pagination ref with the server's pagination info
        if (paginationInfo) {
          pagination.value = {
            currentPage: paginationInfo.current_page,
            perPage: paginationInfo.per_page,
            totalItems: paginationInfo.total_items,
            totalPages: paginationInfo.total_pages,
          };
        }
        
      }
      // This case is no longer needed with the standardized api client
      // Legacy format - direct array
      else if (Array.isArray(result)) {
        fetchedSites = result;
      }
      // Last attempt to find data
      else {
        fetchedSites = [];
      }
      
      // Update sites ref
      sites.value = fetchedSites;
      return fetchedSites;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const createSite = async (siteData) => {
    loading.value = true;
    error.value = null;
    try {
      const newSite = await api.post('sites', siteData);
      sites.value.push(newSite);
      return newSite;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updateSite = async (id, updates) => {
    loading.value = true;
    error.value = null;
    try {
      const updatedSite = await api.put(`sites/${id}`, updates);
      const index = sites.value.findIndex((s) => s.id === id);
      if (index !== -1) {
        sites.value[index] = updatedSite;
      }
      return updatedSite;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const deleteSite = async (id) => {
    loading.value = true;
    error.value = null;
    try {
      await api.delete(`sites/${id}`);
      sites.value = sites.value.filter((s) => s.id !== id);
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const addContact = async (siteId, contactData) => {
    loading.value = true;
    error.value = null;
    try {
      const newContact = await api.post(`sites/${siteId}/contacts`, contactData);
      
      // Update local state
      const siteIndex = sites.value.findIndex((s) => s.id === siteId);
      if (siteIndex !== -1) {
        if (!sites.value[siteIndex].contacts) {
          sites.value[siteIndex].contacts = [];
        }
        sites.value[siteIndex].contacts.push(newContact);
      }
      
      return newContact;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updateContact = async (siteId, contactId, contactData) => {
    loading.value = true;
    error.value = null;
    try {
      const updatedContact = await api.put(`sites/${siteId}/contacts/${contactId}`, contactData);
      
      // Update local state
      const siteIndex = sites.value.findIndex((s) => s.id === siteId);
      if (siteIndex !== -1) {
        const contactIndex = sites.value[siteIndex].contacts.findIndex((c) => c.id === contactId);
        if (contactIndex !== -1) {
          sites.value[siteIndex].contacts[contactIndex] = updatedContact;
        }
      }
      
      return updatedContact;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const removeContact = async (siteId, contactId) => {
    loading.value = true;
    error.value = null;
    try {
      await api.delete(`sites/${siteId}/contacts/${contactId}`);
      
      // Update local state
      const siteIndex = sites.value.findIndex((s) => s.id === siteId);
      if (siteIndex !== -1) {
        sites.value[siteIndex].contacts = 
          sites.value[siteIndex].contacts.filter((c) => c.id !== contactId);
      }
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const downloadBrickTTL = async (siteId) => {
    loading.value = true;
    error.value = null;
    try {
      // Make request to the TTL download endpoint
      const response = await authenticatedFetch(`${API_URL}/sites/${siteId}/brick-ttl/download`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to download TTL: ${response.statusText}`);
      }

      // Get the filename from the Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'site_brick_schema.ttl';
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename="([^"]+)"/);
        if (matches) {
          filename = matches[1];
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return true;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    sites,
    loading,
    error,
    pagination,
    fetchSites,
    createSite,
    updateSite,
    deleteSite,
    addContact,
    updateContact,
    removeContact,
    downloadBrickTTL,
  };
});